import { flow } from 'fp-ts/function';
import { map, orderBy } from 'lodash/fp';
import { createSelector } from 'reselect';
import { AccountType } from '@mews-commander/data-access';
import { getStringArrayProp } from '@mews/fp';
import { composeProps, createPropsExtender } from '@mews/framework';
import { mapId, notEmpty } from '@mews-commander/utils';
import { getCompanies } from '@mews-commander/legacy-entities/src/ducks/companies';
import { getCustomersOrCompaniesFullNames } from '@mews-commander/legacy-entities/src/ducks/customers';
import { getOrderedOpenBillIdsPerAccountIds } from '@mews-commander/legacy-entities/src/selectors/bills';
import { Account } from '../types';
import { getAccountIds } from '../ducks/billing';

interface AccountsOrderData {
    accountsFullNames: Record<string, string>;
    accountsAllBillIds: Record<string, string[]>;
}

const sortAccountIds = flow<[Account[]], Account[], string[]>(
    orderBy<Account>(['hasBills', 'accountName'], ['desc', 'asc']),
    mapId
);

const orderAccounts = (accountIds: string[], { accountsFullNames, accountsAllBillIds }: AccountsOrderData) => {
    const sortOrder = map(
        (accountId) => ({
            hasBills: notEmpty(accountsAllBillIds[accountId]),
            id: accountId,
            accountName: accountsFullNames[accountId]?.toLocaleLowerCase() ?? '',
        }),
        accountIds
    );

    return sortAccountIds(sortOrder);
};

const getAccountsFullNames = composeProps(
    getCustomersOrCompaniesFullNames,
    createPropsExtender('ids', getStringArrayProp('ownerIds'))
);

const prepareAccountsData = createSelector(
    getAccountsFullNames,
    getOrderedOpenBillIdsPerAccountIds,
    (accountsFullNames, accountsAllBillIds) => ({
        accountsFullNames,
        accountsAllBillIds,
    })
);

const prepareOrderedAccountIds = createSelector(getStringArrayProp('ownerIds'), prepareAccountsData, orderAccounts);

export const getOrderedAccountIds = composeProps(
    prepareOrderedAccountIds,
    createPropsExtender('ownerIds', getAccountIds)
);

export const isBillingCustomerOrCompany = createSelector(getAccountIds, getCompanies, (accountIds, companies) => {
    const companyIds = map('id', companies);
    const isCompany = accountIds.every((accountId: string) => companyIds.includes(accountId));

    return isCompany ? AccountType.Company : AccountType.Customer;
});

export const getAccountsData = composeProps(prepareAccountsData, createPropsExtender('ownerIds', getAccountIds));
